import {announce} from '@github-ui/aria-live'
import {CommentBoxButton} from '@github-ui/comment-box/CommentBox'
import {useClosePullRequestMutation} from '@github-ui/mergebox/use-close-pull-request-mutation'
import {useReopenPullRequestMutation} from '@github-ui/mergebox/use-reopen-pull-request-mutation'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {GitPullRequestClosedIcon, GitPullRequestIcon} from '@primer/octicons-react'
import {Tooltip} from '@primer/react'
import {useLayoutRouteData} from '../../page-data/loaders/use-layout-route-data'
import {PullRequestState} from '../../types'
import {isPullRequestOpenState} from '../../utils/pull-request-state'
import type {IssueCommentComposerStateActionProps} from '../comments/IssueCommentComposer'
import {useInvalidatePrState} from './data/use-invalidate-pr-state'
import {useOverviewRouteData} from './data/use-overview-route-data'
import {FailureDialog} from './sidebar-actions/FailureDialog'

const pullRequestStateButtonId = 'pull-request-state-button'

function getButtonText(isClosed: boolean, hasComment: boolean) {
  if (isClosed) return hasComment ? 'Reopen and comment' : 'Reopen pull request'
  return hasComment ? 'Close with comment' : 'Close pull request'
}

export function PullRequestCommentStateAction({hasComment, onSubmit}: IssueCommentComposerStateActionProps) {
  const {user} = useOverviewRouteData()
  const {pullRequest} = useLayoutRouteData()
  const invalidatePrState = useInvalidatePrState()
  const invalidateStateAndRestoreFocus = async () => {
    await invalidatePrState()
    requestAnimationFrame(() => ssrSafeDocument?.getElementById(pullRequestStateButtonId)?.focus())
  }
  const onCloseSuccess = () => {
    announce('Pull request closed')
    void invalidateStateAndRestoreFocus()
  }
  const onReopenSuccess = () => {
    announce('Pull request reopened')
    void invalidateStateAndRestoreFocus()
  }
  const closePullRequestMutation = useClosePullRequestMutation({onSuccess: onCloseSuccess})
  const reopenPullRequestMutation = useReopenPullRequestMutation({onSuccess: onReopenSuccess})
  const isClosed = pullRequest.state === PullRequestState.Closed
  const cannotReopenReason = isClosed && !user?.canReopen ? user?.cannotReopenReason : undefined
  const stateMutation = isClosed ? reopenPullRequestMutation : closePullRequestMutation
  const stateAction = isClosed ? 'reopen' : 'close'

  if (
    isClosed ? !user?.canReopen && !cannotReopenReason : !isPullRequestOpenState(pullRequest.state) || !user?.canClose
  ) {
    return null
  }

  const button = (
    <CommentBoxButton
      aria-disabled={cannotReopenReason ? true : undefined}
      id={pullRequestStateButtonId}
      inactive={Boolean(cannotReopenReason)}
      leadingVisual={isClosed ? GitPullRequestIcon : () => <GitPullRequestClosedIcon className="fgColor-danger" />}
      loading={stateMutation.isPending}
      loadingAnnouncement={isClosed ? 'Reopening pull request' : 'Closing pull request'}
      onClick={() => {
        if (cannotReopenReason) return

        if (isClosed) {
          if (hasComment) onSubmit('reopen', onReopenSuccess)
          else stateMutation.mutate()
        } else if (hasComment) {
          onSubmit('close', onCloseSuccess)
        } else {
          stateMutation.mutate()
        }
      }}
    >
      {getButtonText(isClosed, hasComment)}
    </CommentBoxButton>
  )

  const buttonWithTooltip = cannotReopenReason ? (
    <Tooltip text={cannotReopenReason} type="description" direction="n">
      {button}
    </Tooltip>
  ) : (
    button
  )

  return (
    <>
      {buttonWithTooltip}
      {stateMutation.isError && (
        <FailureDialog
          title={`Failed to ${stateAction} pull request`}
          message={`An error occurred while ${stateAction === 'close' ? 'closing' : 'reopening'} this pull request. Please try again.`}
          details={stateMutation.error.message}
          onRetry={() => stateMutation.mutate()}
          onDismiss={() => stateMutation.reset()}
        />
      )}
    </>
  )
}
